import { RelatedAs } from "../../../../api/stakeholder_relationships";
import {
  Employee,
  LoanAccount,
  PrimaryParty,
  EMPLOYEE_ROLES,
  INLINE_ROW_LIMIT,
} from "../../../../api/creditAccounts";
import {
  isAccountRowValid,
  isAccountsSectionValid,
  isPartyValid,
  isPartiesSectionValid,
  isEmployeeRowValid,
  isEmployeesSectionValid,
} from "../../utils/creditValidation";


/**
 * Pure derivations for the Party-Involved (V5 / Credit) sections. These replace
 * the memoised Redux selectors used previously; the draft now lives in a
 * `useState` group on `CreateCaseForm` and is prop-drilled into the sections.
 */

/** Draft state group for the Credit Party-Involved step (lifted to the parent). */
export interface CreditPartyState {
  loanAccounts: LoanAccount[];
  primaryParties: PrimaryParty[];
}

export interface AccountRelationSuggestion {
  related_as: RelatedAs;
  account_numbers: string[];
}

export interface PartySuggestion{
  name: string;
  account_relationships: AccountRelationSuggestion[];
  selected: boolean;
}

export type SectionKey = "loanAccounts" | "primaryParties" | "employees" | "accounts";

export interface SectionIssue {
  key: SectionKey;
  label: string;
  flagged: boolean; // section not yet valid (incl. "needs at least one row")
  incompleteRows: number;
  hiddenInvalid: number; // invalid rows past the inline limit (only in the modal)
}

export const deriveEmployees = (parties: PrimaryParty[]): Employee[] =>
  parties
    .filter((p) => p.account_relationships.some((r) => r.related_as && EMPLOYEE_ROLES.includes(r.related_as)))
    .map((p) => {
      const employeeRelations = p.account_relationships.filter((r) =>
        r.related_as && EMPLOYEE_ROLES.includes(r.related_as)
      );
      return {
        key: p.id,
        types: employeeRelations.map(r => r.related_as || RelatedAs.OTHER),
        name: p.name,
        employeeId: p.employee_details?.employee_id,
      };
    });

/** Suggestions grouped by loan-account name; `selected` when a party exists. */
export const derivePrimaryBorrowerSuggestions = (
  accounts: LoanAccount[],
  parties: PrimaryParty[],
): PartySuggestion[] => {
  const accountNumbersByAccountName = new Map<string, string[]>();

  accounts.forEach((a) => {
    const name = a.accountName.trim();
    if (!name || !a.extAccountNumber.trim()) return;
    accountNumbersByAccountName.set(name, [...(accountNumbersByAccountName.get(name) ?? []), a.accountNumber]);
  });

  const result = Array.from(accountNumbersByAccountName.entries()).map(([name, account_numbers]) => {
    
    const relationships = [{ related_as: RelatedAs.PRIMARY_BORROWER, account_numbers: account_numbers }]

    const isSelected = parties.some((party) => {
      const isNameMatching = party.name === name;
      const pbAccountNumbers = party.account_relationships.find((r) => r.related_as === RelatedAs.PRIMARY_BORROWER)?.account_numbers || [];
      const isPBForAllAccounts = account_numbers.every((a) => pbAccountNumbers.includes(a));

      return isNameMatching && isPBForAllAccounts;
    })

    return {
      name: name,
      account_relationships: relationships,
      selected: isSelected,
    }
  });

  return result;
};

/** Per-section validity breakdown — powers Add-enable, header badges, and the
 *  validation summary next to Next. Single source, so counts never drift. */
export const deriveSectionIssues = (
  accounts: LoanAccount[],
  parties: PrimaryParty[],
  employees: Employee[]
): SectionIssue[] => [
  {
    key: "loanAccounts",
    label: "Loan Accounts",
    flagged: !isAccountsSectionValid(accounts),
    incompleteRows: accounts.filter((r) => !isAccountRowValid(r, accounts)).length,
    hiddenInvalid: accounts.filter(
      (r, i) => i >= INLINE_ROW_LIMIT && !isAccountRowValid(r, accounts)
    ).length,
  },
  {
    key: "primaryParties",
    label: "Primary Parties",
    flagged: !isPartiesSectionValid(parties),
    incompleteRows: parties.filter((p) => !isPartyValid(p, parties)).length,
    hiddenInvalid: parties.filter(
      (p, i) => i >= INLINE_ROW_LIMIT && !isPartyValid(p, parties)
    ).length,
  },
  {
    key: "employees",
    label: "Employee",
    flagged: !isEmployeesSectionValid(employees),
    incompleteRows: employees.filter((e) => !isEmployeeRowValid(e)).length,
    hiddenInvalid: employees.filter(
      (e, i) => i >= INLINE_ROW_LIMIT && !isEmployeeRowValid(e)
    ).length,
  },
];
